import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Lock, Wifi, Smartphone, Key, Car, Users, Dumbbell, Sun, Waves, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
//hi there, im a comment
const allMembershipAmenities = [
  { text: "LOCKERS (BYO LOCK)", icon: Lock },
  { text: "SHOWERS", icon: Waves },
  { text: "FREE WIFI", icon: Wifi },
  { text: "MOBILE APP", icon: Smartphone },
  { text: "KEYTAG (UPON REQUEST)", icon: Key },
  { text: "ACCESSIBLE PARKING LOTS", icon: Car }
];

const platinumFeatures = [
  { text: "UNLIMITED ACCESS TO ALL GIANT FITNESS LOCATIONS", icon: MapPin },
  { text: "1 GUEST PER DAY", icon: Users },
  { text: "TANNING PRIVILEGES", icon: Sun },
  { text: "MASSAGE CHAIR PRIVILEGES", icon: Dumbbell },
  { text: "POOL PRIVILEGES (VOORHEES LOCATION ONLY)", icon: Waves }
];

const kidsClubAddOn = {
  headline: "Optional Add on Kids Club For Only $30.00 Per Month Per Child*",
  disclaimer:
    "*Kids Club optional add on Voorhees location only.* Kids Cubs online sign up only allows you to add 1 child. If you want to add additional children, please see one of our friendly staff members upon your first check in.",
};

function MembershipCard({
  title,
  enrollmentFee,
  monthlyPrice,
  firstMonthNote,
  benefits,
  benefitsHeading = "Benefits of this Membership Option:",
  isPlatinum,
  isPopular = false,
  delay = 0,
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      className={`relative flex flex-col h-full ${isPlatinum ? "bg-gradient-to-br from-brand-orange/10 to-brand-orange/5 border-2 border-brand-orange" : "bg-white border-2 border-gray-300"} rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow`}
    >
      {isPopular && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-brand-orange text-white px-4 py-1 rounded-full text-xs font-heading font-black uppercase tracking-wider shadow-md">
          Most Popular
        </div>
      )}
      <div className="text-center flex-1">
        <h3 className={`text-xl md:text-2xl font-heading font-black ${isPlatinum ? "text-brand-orange" : "text-gray-900"} mb-6`}>
          {title}
        </h3>

        <div className={`border-b ${isPlatinum ? "border-brand-orange/30" : "border-gray-200"} pb-6 mb-6 space-y-4`}>
          <div>
            <div className="flex items-center justify-center gap-2 mb-1">
              <Gift className={`w-5 h-5 ${isPlatinum ? "text-brand-orange" : "text-gray-600"}`} />
              <span className="text-sm text-gray-600">Enrollment Fee</span>
            </div>
            <p className={`text-3xl font-heading font-black ${isPlatinum ? "text-brand-orange" : "text-gray-900"}`}>
              ${enrollmentFee}
            </p>
          </div>

          <div>
            <div className="flex items-baseline justify-center gap-2">
              <span className="text-2xl text-gray-600">$</span>
              <span className={`text-5xl md:text-6xl font-heading font-black ${isPlatinum ? "text-brand-orange" : "text-gray-900"}`}>
                {monthlyPrice}
              </span>
              <span className="text-xl text-gray-600">/month</span>
            </div>
          </div>

          <p className="text-sm font-semibold text-gray-700">{firstMonthNote}</p>
        </div>

        <div className="text-left mb-6">
          <p className="text-sm font-heading font-black text-gray-900 mb-3 uppercase tracking-wide">
            {benefitsHeading}
          </p>
          <ul className="space-y-2">
            {benefits.map((benefit, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${isPlatinum ? "bg-brand-orange" : "bg-gray-900"}`} />
                {benefit}
              </li>
            ))}
          </ul>
        </div>

        <div className={`border-t ${isPlatinum ? "border-brand-orange/30" : "border-gray-200"} pt-6 mb-6 text-left space-y-2`}>
          <p className="text-xs text-gray-600">$49 Recurring Annual Fee is Due 45 Days From Join Date.</p>
          <p className="text-xs text-gray-600">This membership requires a 60 day notice to cancel.</p>
          <p className="text-xs text-gray-600">Prices Do Not Include Tax.</p>
        </div>

        <div className={`border-t ${isPlatinum ? "border-brand-orange/30" : "border-gray-200"} pt-6 mb-6 text-left space-y-2`}>
          <p className="text-sm font-semibold text-gray-800">{kidsClubAddOn.headline}</p>
          <p className="text-xs text-gray-600">{kidsClubAddOn.disclaimer}</p>
        </div>

        <Link to="/locations">
          <Button
            size="lg"
            className={`w-full ${isPlatinum ? "bg-brand-orange hover:bg-brand-orange-hover text-white shadow-lg" : "bg-gray-900 hover:bg-gray-800 text-white"} font-bold uppercase tracking-wider`}
          >
            <MapPin className="w-5 h-5 mr-2" />
            Choose Location
          </Button>
        </Link>
      </div>
    </motion.div>
  );
}

export default function MembershipsPage() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Gym Membership",
    "provider": {
      "@type": "HealthClub",
      "name": "Giant Fitness"
    },
    "areaServed": {
      "@type": "State",
      "name": ["New Jersey", "Pennsylvania"]
    },
    "offers": [
      {
        "@type": "Offer",
        "name": "Basic Membership",
        "price": "19.99",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "19.99",
          "priceCurrency": "USD",
          "billingIncrement": "P1M"
        }
      },
      {
        "@type": "Offer",
        "name": "Basic Membership (Lower Monthly Rate)",
        "price": "14.99",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "14.99",
          "priceCurrency": "USD",
          "billingIncrement": "P1M"
        }
      },
      {
        "@type": "Offer",
        "name": "Platinum Membership",
        "price": "29.99",
        "priceCurrency": "USD",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "29.99",
          "priceCurrency": "USD",
          "billingIncrement": "P1M"
        }
      }
    ]
  };

  return (
    <div className="min-h-screen pt-20 bg-gradient-to-b from-white to-gray-50" data-testid="memberships-page">
      <SEO
        title="Membership Plans"
        description="Choose the membership that fits your lifestyle. Basic and Platinum no-commitment memberships available with access to state-of-the-art facilities."
        url="/memberships"
        structuredData={structuredData}
      />
      
      {/* Hero Section */}
      <section className="py-16 md:py-24 px-4 md:px-8 text-center bg-gradient-to-b from-gray-50 to-white">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <h1 className="text-3xl sm:text-5xl md:text-7xl font-heading font-black text-gray-900 mb-6" data-testid="memberships-heading">
            MEMBERSHIPS
          </h1>
        </motion.div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-8 pb-20 space-y-20">
        {/* All Memberships Include */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="card-light no-hover p-5 sm:p-8 md:p-10"
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-heading font-black text-gray-900 mb-4">
              ALL GIANT FITNESS MEMBERSHIPS PROVIDE YOU WITH ACCESS TO YOUR FAVORITE FITNESS CENTER
            </h2>
            <p className="text-gray-600 text-lg">
              and include amenities:
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {allMembershipAmenities.map((amenity, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200"
              >
                <div className="w-12 h-12 rounded-lg bg-brand-orange/10 flex items-center justify-center flex-shrink-0">
                  <amenity.icon className="w-6 h-6 text-brand-orange" />
                </div>
                <span className="text-gray-800 font-semibold text-sm">{amenity.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="card-light no-hover p-5 sm:p-8 md:p-10"
        >
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-heading font-black text-gray-900">
              UNLOCK PREMIUM ACCESS WITH OUR PLATINUM MEMBERSHIP:
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {platinumFeatures.map((feature, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex items-center gap-4 p-4 bg-white rounded-lg border border-gray-200"
              >
                <div className="w-12 h-12 rounded-lg bg-brand-orange/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-brand-orange" />
                </div>
                <span className="text-gray-800 font-semibold text-sm">{feature.text}</span>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <p className="text-lg text-gray-800 font-heading font-black mb-2">Not fully commited?</p>
          <p className="text-gray-700">
            All memberships are no-commitment and can be cancelled with 60 days notice.
          </p>
        </motion.section>

        {/* Membership Plans */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white border-2 border-gray-200 rounded-2xl p-8 md:p-10"
        >
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-4">
              Membership Plans
            </h2>
            <p className="text-gray-700 text-lg max-w-2xl mx-auto">
              No-commitment memberships. Upon joining, all new members pay the enrollment fee and their first month&apos;s dues.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            <MembershipCard
              title="Giant Fitness Basic Membership - No Commitment"
              enrollmentFee="59"
              monthlyPrice="19.99"
              firstMonthNote="First Month Dues Paid Upfront Today"
              benefitsHeading="Benefits Of This Membership Option:"
              benefits={["Access To One Selected Giant Fitness Location"]}
              isPlatinum={false}
              delay={0.1}
            />
            <MembershipCard
              title="Giant Fitness Platinum Membership - No Commitment"
              enrollmentFee="1.00"
              monthlyPrice="29.99"
              firstMonthNote="First Month Dues Paid Today"
              benefits={[
                "Unlimited Access to All Giant Fitness Locations",
                "1 Guest Per Day",
                "Tanning Privileges",
                "Massage Chair Privileges",
                "Pool Privileges (Voorhees Location Only)",
              ]}
              isPlatinum={true}
              isPopular={true}
              delay={0.2}
            />
            <MembershipCard
              title="Giant Fitness Basic Membership - No Commitment"
              enrollmentFee="149"
              monthlyPrice="14.99"
              firstMonthNote="First Month Dues Paid Upfront Today"
              benefitsHeading="Benefits Of This Membership Option:"
              benefits={["Access To One Selected Giant Fitness Location"]}
              isPlatinum={false}
              delay={0.3}
            />
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center py-8"
        >
          <p className="text-gray-700 text-lg mb-2">
            Still not sure?{" "}
            <Link to="/free-pass" className="text-brand-orange font-bold underline hover:no-underline">
              Try a 1 day free pass
            </Link>
          </p>
        </motion.section>

        {/* Final CTA Section */}
        <motion.section 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center py-12"
        >
          <h2 className="text-4xl md:text-5xl font-heading font-black text-gray-900 mb-8">
            READY? FIND YOUR LOCATION
          </h2>
          <Link to="/locations">
            <Button size="lg" className="bg-brand-orange hover:bg-brand-orange-hover text-white font-bold uppercase text-xl px-12 py-8 shadow-xl transform hover:scale-105 transition-transform">
              <MapPin className="w-7 h-7 mr-3" />
              Find Your Fitness Center
            </Button>
          </Link>
        </motion.section>
      </div>
    </div>
  );
}
